import { NotificationPayloadT } from 'lib-shared-legacy-types';
import { Sentry } from 'lib-shared-sentry';
import { createStore } from 'redux';

import { notificationActions } from '../../components/notifications/actions/notification';
import {
  AudioMap,
  NOTIFICATION_DEFAULT,
  OFFER_NOTIFICATION_INCOMING,
} from '../../components/notifications/sagas/audio';

const { notificationRemoveByType, notificationAdd } = notificationActions;

export type NotificationSoundTypeT =
  | 'pstnphone'
  | 'widgetchat'
  | 'email'
  | 'messaging'
  | 'notification_sound';

const AUDIO_STATUS_IS_STOPPED = 'AUDIO_STATUS_IS_STOPPED';
const AUDIO_STATUS_WILL_PLAY = 'AUDIO_STATUS_WILL_PLAY';
const AUDIO_STATUS_IS_PLAYING = 'AUDIO_STATUS_IS_PLAYING';

type AudioPlayStatusT =
  | 'AUDIO_STATUS_IS_STOPPED'
  | 'AUDIO_STATUS_WILL_PLAY'
  | 'AUDIO_STATUS_IS_PLAYING';

class AudioManager {
  audioState: {
    [key: string]: {
      audio: HTMLAudioElement;
      status: AudioPlayStatusT;
      shouldStop: boolean;
    };
  };

  audioContextRunning: boolean;

  dispatchFn: null | ReturnType<typeof createStore>['dispatch'];

  constructor() {
    this.audioState = {};
    this.audioContextRunning = false;
    this.dispatchFn = null;
    this.setStore = this.setStore.bind(this);
  }

  setStore(store: ReturnType<typeof createStore>) {
    this.dispatchFn = store.dispatch;
  }

  dispatch(action: { type: string }) {
    if (typeof this.dispatchFn === 'function') {
      return this.dispatchFn(action);
    }
    return null;
  }

  play(sound: any, type: NotificationSoundTypeT, shouldLoop = true) {
    if (AudioMap[sound]) {
      this.playAudio(AudioMap[sound], shouldLoop);
    } else if (sound === null) {
      if (type === 'notification_sound') {
        this.playAudio(AudioMap[NOTIFICATION_DEFAULT], false);
      } else {
        this.playAudio(AudioMap[OFFER_NOTIFICATION_INCOMING], shouldLoop);
      }
    }
  }

  stopFile(audioFile: string) {
    if (!this.audioState[audioFile].audio) return;

    if (this.audioState[audioFile].status === AUDIO_STATUS_IS_PLAYING) {
      this.audioState[audioFile].audio.pause();
      this.audioState[audioFile].audio.currentTime = 0;
      this.audioState[audioFile].status = AUDIO_STATUS_IS_STOPPED;
      this.audioState[audioFile].shouldStop = false;
    } else if (this.audioState[audioFile].status === AUDIO_STATUS_WILL_PLAY) {
      this.audioState[audioFile].shouldStop = true;
    }
  }

  stop() {
    for (const audioFile in this.audioState) {
      if (this.audioState[audioFile]) {
        this.stopFile(audioFile);
      }
    }
  }

  checkAudioContext() {
    if (this.audioContextRunning) return true;

    const audioContext = new AudioContext();
    this.audioContextRunning = audioContext.state === 'running';

    if (!this.audioContextRunning) {
      // https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
      // because chrome blocks autoplay we dispatch a notification to trigger
      // a user interaction which should open up for playing audio
      const notificationOptions: NotificationPayloadT = {
        type: 'soundPermissionDenied',
        text: `By clicking "Allow" below you are allowing Dixa to play sounds.`,
        title: 'Sound not enabled yet',
        className: 'warning',
        showDesktop: true,
        skipNotificationSound: true,
        icon: 'info',
        rightAction: {
          text: 'Allow',
          action: () => {
            audioContext.resume();
            this.audioContextRunning = true;
            this.dispatch(
              notificationRemoveByType({
                type: 'soundPermissionDenied',
              }),
            );
          },
        },
      };

      this.dispatch(notificationAdd(notificationOptions));
    }

    return this.audioContextRunning;
  }

  playAudio(audioFile: string, loop = true) {
    if (!this.checkAudioContext()) return;

    if (!this.audioState[audioFile]) {
      this.audioState[audioFile] = {
        audio: new Audio(audioFile),
        status: AUDIO_STATUS_WILL_PLAY,
        shouldStop: false,
      };
    } else if (this.audioState[audioFile].status === AUDIO_STATUS_IS_PLAYING) {
      this.stopFile(audioFile);
    }

    this.audioState[audioFile].status = AUDIO_STATUS_WILL_PLAY;
    this.audioState[audioFile].audio.currentTime = 0;
    this.audioState[audioFile].audio.loop = loop;
    this.audioState[audioFile].audio
      .play()
      .then(() => {
        this.audioState[audioFile].status = AUDIO_STATUS_IS_PLAYING;
        if (this.audioState[audioFile].shouldStop) {
          this.stopFile(audioFile);
        }
      })
      .catch((err: Error) => {
        console.error('AUDIO ERROR', err);
        Sentry.captureException(err, {
          level: 'error',
          extra: {
            audio: this.audioState[audioFile].audio.src,
          },
        });
      });
  }
}

export default new AudioManager();
